import React, { useRef, useEffect, useCallback, useState } from 'react';
import { SpellCheckMatch } from '@/services/spellCheckerService';
import { isSpellingError, isGrammarError, isStyleError } from '@/utils/spellCheckerUtils';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  spellCheckResults: SpellCheckMatch[];
  highlightedWord?: { offset: number; length: number } | null;
  placeholder?: string;
  className?: string;
  onWordClick?: (offset: number, length: number) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  spellCheckResults,
  highlightedWord,
  placeholder = "Enter Essay Text",
  className = "",
  onWordClick,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeHighlight, setActiveHighlight] = useState<{ offset: number; length: number } | null>(null);

  // Update editor content when value changes externally
  useEffect(() => {
    if (editorRef.current && !isUpdating) {
      const currentText = editorRef.current.textContent || '';
      if (currentText !== value) {
        editorRef.current.textContent = value;
      }
    }
  }, [value, isUpdating]);


  // Handle editor input
  const handleInput = useCallback(() => {
    if (editorRef.current && !isUpdating) {
      setIsUpdating(true);
      const newValue = editorRef.current.textContent || '';
      onChange(newValue);
      setIsUpdating(false);
    }
  }, [onChange, isUpdating]);

  // Handle word highlighting from sidebar
  useEffect(() => {
    if (highlightedWord && editorRef.current) {
      const { offset, length } = highlightedWord;
      const textContent = editorRef.current.textContent || '';
      
      if (offset + length <= textContent.length) {
        setActiveHighlight(highlightedWord);
        
        const range = document.createRange();
        const textNode = editorRef.current.firstChild || editorRef.current;
        
        try {
          range.setStart(textNode, offset);
          range.setEnd(textNode, offset + length);
          
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
          
          // Scroll to the selection
          editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          console.warn('Could not highlight word:', e);
        }
      }
    } else {
      setActiveHighlight(null);
    }
  }, [highlightedWord]);

  // Apply error highlighting with improved logic
  useEffect(() => {
    if (!editorRef.current || isUpdating) return;

    // Always reset content
    const textContent = editorRef.current.textContent || '';
   

    if (!spellCheckResults.length) return;

    // Sort errors by offset in descending order to apply from end to start
    const sortedErrors = [...spellCheckResults].sort((a, b) => b.offset - a.offset);

    // Apply new highlights from end to start
    sortedErrors.forEach(match => {
      if (match.offset + match.length <= textContent.length) {
        try {
          const range = document.createRange();
          const textNode = editorRef.current!.firstChild || editorRef.current!;
          
          range.setStart(textNode, match.offset);
          range.setEnd(textNode, match.offset + match.length);
          
          const span = document.createElement('span');
          const isSpellingErrorResult = isSpellingError(match);
          const isGrammarErrorResult = isGrammarError(match);
          const isStyleErrorResult = isStyleError(match);
          
          // Determine error type for styling
          let errorType = 'grammar'; // default
          if (isSpellingErrorResult) errorType = 'spelling';
          else if (isStyleErrorResult) errorType = 'style';
          
          // Check if this is the active highlighted word
          const isActiveWord = activeHighlight && 
            match.offset === activeHighlight.offset && 
            match.length === activeHighlight.length;
          
          // Apply styling based on whether it's the active word
          if (isActiveWord) {
            // Active word gets bright highlighting
            span.className = 'error-highlight active-word';
            if (isSpellingErrorResult) {
              // Active spelling error - bright red
              span.style.backgroundColor = '#fecaca';
              span.style.textDecoration = 'underline wavy #dc2626';
              span.style.boxShadow = '0 0 8px rgba(239, 68, 68, 0.3)';
            } else if (isGrammarErrorResult) {
              // Active grammar error - bright yellow
              span.style.backgroundColor = '#fde68a';
              span.style.textDecoration = 'underline wavy #d97706';
              span.style.boxShadow = '0 0 8px rgba(234, 179, 8, 0.3)';
            } else if (isStyleErrorResult) {
              // Active style error - bright purple
              span.style.backgroundColor = '#ede9fe';
              span.style.textDecoration = 'underline wavy #6366f1';
              span.style.boxShadow = '0 0 8px rgba(99, 102, 241, 0.3)';
            }
            span.style.textDecorationThickness = '3px';
            span.style.textUnderlineOffset = '2px';
          } else {
            // Regular error styling
            if (isSpellingErrorResult) {
              span.className = 'error-highlight spelling-error';
              span.style.backgroundColor = '#fee2e2';
              span.style.textDecoration = 'underline wavy #ef4444';
              span.style.textDecorationThickness = '2px';
              span.style.textUnderlineOffset = '2px';
            } else if (isGrammarErrorResult) {
              span.className = 'error-highlight grammar-error';
              span.style.backgroundColor = '#fef9c3';
              span.style.textDecoration = 'underline wavy #eab308';
              span.style.textDecorationThickness = '2px';
              span.style.textUnderlineOffset = '2px';
            } else if (isStyleErrorResult) {
              span.className = 'error-highlight style-error';
              span.style.backgroundColor = '#f3e8ff';
              span.style.textDecoration = 'underline wavy #8b5cf6';
              span.style.textDecorationThickness = '2px';
              span.style.textUnderlineOffset = '2px';
            }
            
            // Dim other words when there's an active highlight
            if (activeHighlight) {
              span.style.opacity = '0.4';
              span.style.filter = 'grayscale(30%)';
            }
          }
          
          span.style.cursor = 'pointer';
          span.style.borderRadius = '2px';
          span.style.padding = '1px 2px';
          span.style.display = 'inline';
          
          span.onclick = () => {
            if (onWordClick) {
              onWordClick(match.offset, match.length);
            }
          };
          
          span.title = match.message;
          
          try {
            range.surroundContents(span);
          } catch (e) {
            console.warn('Could not highlight error:', e);
          }
        } catch (e) {
          console.warn('Error applying highlight:', e);
        }
      }
    });
  }, [spellCheckResults, onWordClick, isUpdating, activeHighlight]);

  return (
    <div className={`${className} relative`}>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[130px] overflow-y-auto"
        style={{ 
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
        }}
        data-placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor; 